import * as aws4 from 'aws4';
import MissingCredentialsError from '../exception/auth/missingCredentialsError';
import { underscoreKeys } from '../lib/functions';

export default class RestApiDao {
  constructor(logger, httpClient, store, config) {
    this.logger = logger;
    this.httpClient = httpClient;
    this.store = store;
    this.aws4 = aws4;
    this.apiPath = config.apiBasePath;
    this.host = config.apiUrl;
    this.protocol = config.apiProtocol;
  }

  _buildRequest(method, path, data, params = null) {
    const thisPath = path[0] === '/' ? `${this.apiPath}${path}` : `${this.apiPath}/${path}`;

    let queryString = '';
    if (params) {
      const urlParams = new URLSearchParams();
      Object.keys(params).map(k => urlParams.append(k, params[k]));
      queryString = `?${urlParams.toString()}`;
    }

    const request = {
      host: this.host,
      method,
      url: `${this.protocol}://${this.host}${thisPath}${queryString}`,
      path: `${thisPath}${queryString}`,
    };

    if (data) {
      request.data = data;
      request.body = JSON.stringify(data);
    }

    if (['PUT', 'POST', 'PATCH'].indexOf(method) > -1) {
      request.headers = {
        'content-type': 'application/json',
      };
    }

    return request;
  }

  _signRequest(method, path, data, params = null) {
    const tokens = this.store.getters.tokens;
    const credentials = {
      secretAccessKey: tokens.secretKey,
      accessKeyId: tokens.accessKeyId,
      sessionToken: tokens.sessionToken,
    };
    if (!credentials.secretAccessKey) {
      throw new MissingCredentialsError();
    }
    const ret = this.aws4.sign(this._buildRequest(method, path, data, params), credentials);
    delete ret.headers.Host;
    delete ret.headers['Content-Length'];

    return ret;
  }

  getMember(memberId) {
    return this.httpGet(`member/${memberId}`);
  }

  refreshToken(memberId, idToken, refreshToken, accessToken) {
    return this.httpPostAnonymously('auth/refresh', underscoreKeys({
      memberId, idToken, refreshToken, accessToken,
    }));
  }

  getImportStatus(memberId) {
    return this.httpGet(`import/status?member_id=${memberId}`);
  }

  updateCalendarDay(memberId, date, data) {
    const d = data;
    d.date_ = date;
    return this.httpPost(`calendar/${memberId}/calendar-day`, d);
  }

  getAveragePowerScales(memberId, criticalPower) {
    return this.httpGet(`calendar/${memberId}/average-power-scales?critical_power=${criticalPower}`);
  }

  async getMemberByAwsCognitoSub(awsCognitoSub) {
    const response = await this.httpGet(`member/by-cognito-sub/${awsCognitoSub}`);
    return response.data.member[0];
  }

  sendEmail(data) {
    return this.httpPost('/email/send', data);
  }

  updateMember(member) {
    return this.httpPost(`member/${member.memberId}`, underscoreKeys(member));
  }

  addMember(payload) {
    return this.httpPut('member', payload);
  }

  addAndConfirmMember(payload) {
    return this.httpPut('member/create-and-confirm', payload);
  }

  getCoupon(couponCode) {
    return this.httpGetAnonymously(`payment/coupon/${couponCode}`);
  }

  updatePayment(payload) {
    const memberId = payload.member_id;
    return this.httpPost(`member/${memberId}/payment`, payload);
  }

  getPayment(memberId) {
    return this.httpGet(`member/${memberId}/payment`);
  }

  cancelSubscription(memberId) {
    return this.httpDelete(`member/${memberId}/payment`);
  }

  deleteAccount(memberId) {
    return this.httpDelete(`member/${memberId}`);
  }

  // startDate and endDate must be strings in YYYY-MM-DD format.
  getActivities(
    memberId,
    startDate = null,
    endDate = null,
    returnType = null,
    cognitoId = null,
    flaggedAsDuplicateSince = null,
    archives = null,
  ) {
    const querystring = RestApiDao.getActivitiesQuerystring(
      startDate, endDate, returnType, cognitoId, flaggedAsDuplicateSince, archives,
    );
    return this.httpGet(`member/${memberId}/activities${querystring}`);
  }

  // startDate and endDate must be strings in YYYY-MM-DD format.
  getActivitiesByFollower(memberId, startDate = null, endDate = null, returnType = null) {
    const querystring = RestApiDao.getActivitiesQuerystring(startDate, endDate, returnType);
    return this.httpGet(`member/${memberId}/following-activities${querystring}`);
  }

  getSamples(activityId, returnType = null) {
    const params = Object.assign(
      { subset: true },
      returnType && { return_type: returnType },
    );

    return this.httpGet(`activity/${activityId}/samples`, params);
  }

  getFollowees(memberId) {
    return this.httpGet(`member/${memberId}/followees`);
  }

  saveTeam(team) {
    return this.httpPatch(`team/${team.team_id}`, {
      name: team.name,
      athletes_can_view_dashboard: team.athletes_can_view_dashboard,
    });
  }

  addMemberToTeam(memberId, teamId) {
    return this.httpPatch(`team/${teamId}/members/${memberId}`);
  }

  addActivityNote(activityId, message, invalidatesActivity = true) {
    return this.httpPost(`activity/${activityId}/activity-note`, {
      message,
      invalidates_activity: invalidatesActivity,
    });
  }

  deleteActivityNotes(activityNoteIds) {
    return this.httpPost('delete-activity-notes', {
      activity_note_ids: activityNoteIds,
    });
  }

  deleteActivities(activityIds) {
    return this.httpPost('delete-activities', {
      activity_ids: activityIds,
    });
  }

  // @param payload is json of the form:
  //    {
  //         'action': 'load_activities_for_members'
  //         'params': {
  //             'members': [
  //                 {
  //                     'member_id': <int>, # The member_id to load activities for
  //                     'use_most_recent_activity_date': bool -- If set, ignore start and end dates
  //                     'start_date': 'YYYY-MM-DD', # Optional start date
  //                     'end_date': 'YYYY-MM-DD' # Optional end date
  //                 },
  //                 {
  //                     'member_id': <int>, # The member_id to load activities for
  //                     'use_most_recent_activity_date': bool -- If set, ignore start and end dates
  //                     'start_date': 'YYYY-MM-DD', # Optional start date
  //                     'end_date': 'YYYY-MM-DD' # Optional end date
  //                 },
  //                 ...
  //             ]
  //         }
  //     }
  requestSuuntoActivityRefresh(payload) {
    return this.httpPost('suunto/pull', payload, 'suunto');
  }

  // Diary endpoints
  getDailyDiaries(memberId, cognitoId, startDate = null, endDate = null) {
    const querystring = RestApiDao.getDiariesQuerystring(
      startDate, endDate, cognitoId,
    );
    return this.httpGet(`member/${memberId}/diaries${querystring}`);
  }

  //
  // Diary comment endpoints.
  //

  // @param diaryDate should be in the format YYYYMMDD
  addDiaryComment(diaryDate, diaryMemberId, commenterId, comment) {
    const payload = {
      diary_member_id: diaryMemberId,
      commenter_member_id: commenterId,
      comment,
    };

    return this.httpPost(`diary/date/${diaryDate}/comment`, payload);
  }

  updateDiaryComment(diaryId, commenterId, commentId, comment) {
    const payload = {
      comment,
      commenter_id: commenterId,
    };

    return this.httpPost(`diary/$(diaryId}/comment/${commentId}`, payload);
  }

  deleteDiaryComment(diaryId, commentId) {
    return this.httpDelete(`/diary/${diaryId}/comment/${commentId}`);
  }

  logNotification(memberId, noticeId) {
    return this.httpPut(`/member/${memberId}/notification/${noticeId}`, {});
  }

  // @param diaryDate should be in the format YYYYMMDD
  getDiaryComments(diaryDate, memberId) {
    return this.httpGet(`diary/date/${diaryDate}/comment?member=${memberId}`);
  }

  //
  // Training plan endpoints.
  //

  // @param startDate should be in the format YYYYMMDD
  // @param endDate should be in the format YYYYMMDD
  getTrainingPlans(startDate, endDate, memberId) {
    return this.httpGet(`member/${memberId}/training-plans?start_date=${startDate}&end_date=${endDate}`);
  }

  getActivityTypes() {
    return this.httpGet('activity-types');
  }

  // @param diaryDate should be in the format YYYYMMDD
  saveTrainingPlan(diaryDate, plan, diaryMemberId, curMemberId = null, removedById = null) {
    const payload = Object.assign({}, plan, {
      removed_by: removedById,
      modifying_member_id: curMemberId,
      diary_member_id: diaryMemberId,
    });

    return this.httpPost(`diary/date/${diaryDate}/training-plan`, payload);
  }

  // Notices
  getNotices(memberId) {
    return this.httpGet(`member/${memberId}/notice`);
  }

  getCalendar(memberId, cognitoSub) {
    return this.httpGet(`calendar/${memberId}`, { cognito_user_id: cognitoSub });
  }

  getRoster(teamId) {
    return this.httpGet(`team/${teamId}/roster`);
  }

  addUserToCohort(memberId, cohortId, permissions) {
    return this.httpPost('cohort-members', {
      member_id: memberId,
      cohort_id: cohortId,
      permissions,
    });
  }

  // Private functions
  static getActivitiesQuerystring(
    startDate,
    endDate,
    returnType,
    cognitoId,
    flaggedAsDuplicateSince,
    archives,
  ) {
    let querystring = '';
    const params = [];
    if (startDate !== null && endDate !== null) {
      params.push(`start_date=${startDate}`);
      params.push(`end_date=${endDate}`);
    }
    if (typeof returnType !== 'undefined' && returnType !== null && returnType !== '') {
      params.push(`return_type=${returnType}`);
    }
    if (cognitoId) {
      params.push(`cognito_user_id=${cognitoId}`);
    }
    if (archives) {
      params.push('archives=1');
    } else if (flaggedAsDuplicateSince) {
      params.push(`flagged_as_duplicate_since=${flaggedAsDuplicateSince}`);
    }
    if (params.length > 0) {
      querystring = `?${params.join('&')}`;
    }
    return querystring;
  }

  static getDiariesQuerystring(startDate, endDate, cognitoId) {
    let querystring = '';
    const params = [];
    if (startDate !== null && endDate !== null) {
      params.push(`start_date=${startDate}`);
      params.push(`end_date=${endDate}`);
    }
    if (cognitoId) {
      params.push(`cognito_user_id=${cognitoId}`);
    }
    if (params.length > 0) {
      querystring = `?${params.join('&')}`;
    }
    return querystring;
  }

  async checkToken() {
    if (window.platform
      && this.store.getters.isAuthenticated
      && this.store.getters.tokensExpired()
    ) {
      await this.store.dispatch('refreshToken');
    }
  }

  async httpGet(url, params = null) {
    try {
      await this.checkToken();
      return await this.httpClient.request(this._signRequest('GET', url, null, params));
    } catch (e) {
      // AWS lambda has a 6MB limit on responses, so if we've requested anything with
      // a payload larger than that it will return a 303 status code, which means the
      // response body will contain a pre-signed s3 url that we should fetch instead.
      if (e.response && e.response.status === 303) {
        return this.httpClient.get(e.response.data.location);
      }
      throw e;
    }
  }

  async httpPut(url, data) {
    await this.checkToken();
    return this.httpClient.request(this._signRequest('PUT', url, data));
  }

  async httpPost(url, data = {}) {
    await this.checkToken();
    return this.httpClient.request(this._signRequest('POST', url, data));
  }

  async httpDelete(url) {
    await this.checkToken();
    return this.httpClient.request(this._signRequest('DELETE', url));
  }

  async httpPatch(url, data = {}) {
    await this.checkToken();
    return this.httpClient.request(this._signRequest('PATCH', url, data));
  }

  httpPutAnonymously(url, data) {
    return this.httpClient.put(url, data);
  }

  httpGetAnonymously(url) {
    return this.httpClient.get(url);
  }

  httpPostAnonymously(url, data) {
    return this.httpClient.post(url, data);
  }
}
